import React from "react";
import styled from "styled-components/native";
import { Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import useScreenOptionsHooks from "../../../../../hooks/useScreenOptionsHooks";
import QuantitySelector from "@components/molecules/QuantitySelector";
import currency from "currency.js";
import { css } from "styled-components/native";

export type UserSelectorProps = {
  id: number;
  pricePerAdditionalUser: number;
  value: number;
  onChange: (count: number) => void;
  item: any;
  type?: "purchase_seats" | "manage_plan";
  is_saas_plan?: boolean;
  fromPurchaseSeats?: boolean;
  disabled?: boolean;
};

const UserSelector: React.FC<UserSelectorProps> = ({
  id,
  pricePerAdditionalUser,
  value,
  onChange,
  item,
  type,
  is_saas_plan,
  fromPurchaseSeats = false,
  disabled = false,
}) => {
  const { theme, typography, t } = useScreenOptionsHooks();

  const PriceComponent = () => {
    return (
      <AdditionalUserPriceContainer fromPurchaseSeats={fromPurchaseSeats}>
        <VDBText
          type={typography["Body-M"]}
          color={disabled ? theme["core-04"] : theme["core-05"]}
        >
          {disabled
            ? currency(0, {
                symbol: item?.additional_user_plan?.currency_symbol,
                precision: 2,
              }).format()
            : currency(pricePerAdditionalUser, {
                symbol: item?.additional_user_plan?.currency_symbol,
                precision: 2,
              }).format()}
        </VDBText>
      </AdditionalUserPriceContainer>
    );
  };
  return (
    <Container
      id={id != null ? String(id) : undefined}
      applyBorder={type === "purchase_seats"}
      fromPurchaseSeats={fromPurchaseSeats}
      disabled={disabled}
    >
      <TextContainer fromPurchaseSeats={fromPurchaseSeats}>
        <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
          {type === "purchase_seats" && !is_saas_plan
            ? t("purchaseSeats.popUp.users.text", "Users")
            : t("managePlan.popUp.additionalUsers.text", "Additional Users")}
        </VDBText>
        <Spacer size={SpaceType.Basic} />
        <UserContainer>
          {item?.additional_user_plan?.discounted_price && (
            <>
              <VDBText
                type={typography["Body-R"]}
                color={theme["error-01"]}
                textDecoration="line-through"
              >
                +{item?.additional_user_plan?.currency_symbol}
                {item.additional_user_plan.base_price}
              </VDBText>
              <Spacer size={SpaceType.ExtraSmall} />
            </>
          )}
          <VDBText
            type={typography["Body-M"]}
            color={
              item?.additional_user_plan?.discounted_price
                ? theme["success-01"]
                : theme["core-05"]
            }
          >
            +{item?.additional_user_plan?.currency_symbol}
            {item?.additional_user_plan?.discounted_price ??
              item?.additional_user_plan?.base_price}
          </VDBText>
          <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
            {t("managePlan.popUp.userPerYear.text", "/user/year")}
          </VDBText>
        </UserContainer>
      </TextContainer>
      {fromPurchaseSeats && <Spacer size={SpaceType.Extra} />}
      <StepperRow disabled={disabled} fromPurchaseSeats={fromPurchaseSeats}>
        <QuantitySelector
          defaultValue={value}
          onChange={onChange}
          min={0}
          disabled={disabled}
        />
        {fromPurchaseSeats && <PriceComponent />}
      </StepperRow>
      {!fromPurchaseSeats && <PriceComponent />}
    </Container>
  );
};

const Container = styled.View<{ applyBorder?: boolean }>`
  flex-direction: ${({ fromPurchaseSeats }: { fromPurchaseSeats?: boolean }) =>
    !fromPurchaseSeats ? "row" : "column"};
  ${({ fromPurchaseSeats }: { fromPurchaseSeats?: boolean }) =>
    !fromPurchaseSeats &&
    css`
      justify-content: space-between;
    `}
  background-color: ${({
    theme,
    fromPurchaseSeats,
    disabled,
  }: {
    disabled?: boolean;
    theme: any;
    fromPurchaseSeats?: boolean;
  }) =>
    fromPurchaseSeats
      ? theme["core-01"]
      : !disabled
      ? theme["core-01"]
      : theme["core-02"]};
  border-radius: ${SpaceType.Basic}px;
  padding: ${SpaceType.Extra}px;
  ${({ applyBorder, theme }: { applyBorder?: boolean; theme: any }) =>
    applyBorder &&
    css`
      border-width: 1px;
      border-color: ${theme["core-04"]};
      border-radius: ${SpaceType.Basic}px;
    `}
`;

const StepperRow = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${SpaceType.Extra}px;
  background-color: ${({
    theme,
    fromPurchaseSeats,
    disabled,
  }: {
    disabled?: boolean;
    theme: any;
    fromPurchaseSeats?: boolean;
  }) =>
    fromPurchaseSeats
      ? theme["core-02"]
      : !disabled
      ? theme["core-01"]
      : theme["core-02"]};
  border-radius: ${SpaceType.Basic}px;
`;

const UserContainer = styled.View`
  flex-direction: row;
  align-items: center;
`;

const TextContainer = styled.View`
  flex-direction: ${({ fromPurchaseSeats }: { fromPurchaseSeats?: boolean }) =>
    !fromPurchaseSeats ? "column" : "row"};
  ${({ fromPurchaseSeats }: { fromPurchaseSeats?: boolean }) =>
    fromPurchaseSeats &&
    css`
      align-items: center;
    `}
  justify-content: ${({ fromPurchaseSeats }: { fromPurchaseSeats?: boolean }) =>
    !fromPurchaseSeats ? "center" : "space-between"};
`;

const AdditionalUserPriceContainer = styled.View`
  flex-direction: column;
  gap: ${SpaceType.ExtraSmall}px;
  align-items: center;
  ${({ fromPurchaseSeats }: { fromPurchaseSeats?: boolean }) =>
    !fromPurchaseSeats &&
    css`
      justify-content: center;
    `}
`;
export default UserSelector;
